<template>
  <div class="view-page">

    <title-bar title="Portal web" />

    <div class="wp-body">

      <!-- Recursos visuales -->
      <section class="wp-section">
        <div class="wp-section__head">
          <h2 class="wp-section__title">Recursos visuales</h2>
          <p class="wp-section__desc">Logotipo y favicon que se muestran en tu portal de onboarding.</p>
        </div>
        <div class="wp-section__content">
          <div class="wp-asset">
            <div class="wp-asset__top">
              <span class="wp-asset__label">Logotipo</span>
              <button class="wp-btn-edit" @click="showModalLogo = true">
                <i class="far fa-edit"></i> Actualizar
              </button>
            </div>
            <div class="wp-asset__preview">
              <img v-if="assets.logo" :src="assets.logo" alt="Logo" class="wp-logo" />
              <span v-else class="wp-asset__empty">Sin logotipo</span>
            </div>
            <div v-if="assets.logoLink" class="wp-asset__link">
              <i class="fas fa-link"></i>
              <a :href="assets.logoLink" target="_blank">{{ assets.logoLink }}</a>
            </div>
          </div>

          <div class="wp-divider" />

          <div class="wp-asset">
            <div class="wp-asset__top">
              <span class="wp-asset__label">Favicon</span>
              <button class="wp-btn-edit" @click="showModalFavi = true">
                <i class="far fa-edit"></i> Actualizar
              </button>
            </div>
            <div class="wp-asset__preview wp-asset__preview--small">
              <img v-if="assets.favicon" :src="assets.favicon" alt="Favicon" class="wp-favicon" />
              <span v-else class="wp-asset__empty">Sin favicon</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Paleta de colores -->
      <section class="wp-section">
        <div class="wp-section__head">
          <h2 class="wp-section__title">Paleta de colores</h2>
          <p class="wp-section__desc">Colores aplicados en la interfaz de tu portal.</p>
        </div>
        <div class="wp-section__content">
          <div class="wp-colors-header">
            <button class="wp-btn-edit" @click="showModalColors = true">
              <i class="far fa-edit"></i> Configurar colores
            </button>
          </div>
          <div class="wp-color-row">
            <span class="wp-color-row__label">Color principal</span>
            <div class="wp-color-row__value">
              <span v-if="assets.colors.principal" class="wp-swatch" :style="{ backgroundColor: assets.colors.principal }"></span>
              <span class="wp-color-row__hex">{{ assets.colors.principal || '—' }}</span>
            </div>
          </div>
          <div class="wp-divider" />
          <div class="wp-color-row">
            <span class="wp-color-row__label">Color secundario</span>
            <div class="wp-color-row__value">
              <span v-if="assets.colors.secondary" class="wp-swatch" :style="{ backgroundColor: assets.colors.secondary }"></span>
              <span class="wp-color-row__hex">{{ assets.colors.secondary || '—' }}</span>
            </div>
          </div>
          <div class="wp-divider" />
          <div class="wp-color-row">
            <span class="wp-color-row__label">Color botones principales</span>
            <div class="wp-color-row__value">
              <span v-if="assets.colors.buttons" class="wp-swatch" :style="{ backgroundColor: assets.colors.buttons }"></span>
              <span class="wp-color-row__hex">{{ assets.colors.buttons || '—' }}</span>
            </div>
          </div>
          <div class="wp-divider" />
          <div class="wp-color-row">
            <span class="wp-color-row__label">Color botones secundarios</span>
            <div class="wp-color-row__value">
              <span v-if="assets.colors.buttonsSecondary" class="wp-swatch" :style="{ backgroundColor: assets.colors.buttonsSecondary }"></span>
              <span class="wp-color-row__hex">{{ assets.colors.buttonsSecondary || '—' }}</span>
            </div>
          </div>
          <div class="wp-divider" />
          <div class="wp-color-row">
            <span class="wp-color-row__label">Color header</span>
            <div class="wp-color-row__value">
              <span v-if="assets.colors.header" class="wp-swatch" :style="{ backgroundColor: assets.colors.header }"></span>
              <span class="wp-color-row__hex">{{ assets.colors.header || '—' }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- URLs -->
      <section class="wp-section">
        <div class="wp-section__head">
          <h2 class="wp-section__title">URLs del portal</h2>
          <p class="wp-section__desc">Accesos directos a los módulos de tu plataforma.</p>
        </div>
        <div class="wp-section__content">
          <div class="wp-url-row">
            <span class="wp-url-row__label">Onboarding</span>
            <a v-if="websitesUrls.onboarding" :href="websitesUrls.onboarding" target="_blank" class="wp-url-row__link">
              {{ websitesUrls.onboarding }}
            </a>
            <span v-else class="wp-url-row__empty">—</span>
          </div>
          <template v-if="websitesUrls.onboarding">
            <div class="wp-divider" />
            <div class="wp-url-row">
              <span class="wp-url-row__label">Portal brokers</span>
              <a :href="`${websitesUrls.onboarding}/brokers`" target="_blank" class="wp-url-row__link">
                {{ websitesUrls.onboarding }}/brokers
              </a>
            </div>
          </template>
          <template v-if="websitesUrls.onboardingGuest">
            <div class="wp-divider" />
            <div class="wp-url-row">
              <span class="wp-url-row__label">Onboarding invitados</span>
              <a :href="websitesUrls.onboardingGuest" target="_blank" class="wp-url-row__link">
                {{ websitesUrls.onboardingGuest }}
              </a>
            </div>
          </template>
          <template v-if="websitesUrls.factoringPortal">
            <div class="wp-divider" />
            <div class="wp-url-row">
              <span class="wp-url-row__label">Portal Factoraje</span>
              <a :href="websitesUrls.factoringPortal" target="_blank" class="wp-url-row__link">
                {{ websitesUrls.factoringPortal }}
              </a>
            </div>
          </template>
          <template v-if="websitesUrls.creditPortal">
            <div class="wp-divider" />
            <div class="wp-url-row">
              <span class="wp-url-row__label">Portal de Créditos</span>
              <a :href="websitesUrls.creditPortal" target="_blank" class="wp-url-row__link">
                {{ websitesUrls.creditPortal }}
              </a>
            </div>
          </template>
          <template v-if="websitesUrls.smartlinks">
            <div class="wp-divider" />
            <div class="wp-url-row">
              <span class="wp-url-row__label">Smartlinks</span>
              <a :href="websitesUrls.smartlinks" target="_blank" class="wp-url-row__link">
                {{ websitesUrls.smartlinks }}
              </a>
            </div>
          </template>
        </div>
      </section>

    </div>
    <modal-component
      :active="showModalLogo"
      @cancel="showModalLogo = false"
      @hdlOnClick="submitLogoUpload"
      textSave="Subir"
      titleModal="Subir logotipo"
      optionalText="El logotipo se utiliza en correos electrónicos, documentos contractuales y en el portal de onboarding."
      :disabled="!logoFile"
      :loader="loadingLogo"
    >
      <div class="-mb-4 -mt-2 pay-content">
        <UploadInput class="-mt-2" @file-selected="handleLogoChange" :maxSize="1" />

        <div class="logo-link-section">
          <span class="description">Link del Logo en el Onboarding</span>
          <input class="input"
          type="url"
          name="logoLink"
          placeholder="https://ejemplo.com"
          autocomplete="off"
          v-model="logoLink"
          >
          <div class="modal-notice">
            <i class="fas fa-info-circle modal-notice__icon"></i>
            <span>El link del logo no es obligatorio. Si no lo agregas, el link default redirige a la página principal del onboarding.</span>
          </div>
        </div>
      </div>
    </modal-component>

    <modal-component
      :active="showModalFavi"
      @cancel="showModalFavi = false"
      @hdlOnClick="submitFaviUpload"
      :textSave="`Subir`"
      titleModal="Subir favicon"
      optionalText="El favicon se muestra en el portal de onboarding."
      :disabled="!faviconFile"
      :loader="loadingFavicon"
    >
      <div class="-mb-4 -mt-2 pay-content">
        <UploadInput class="-mt-2" @file-selected="handleFaviconChange" :maxSize="0.5" :validateDimensions="true" :maxWidth="32" :maxHeight="32" />
      </div>
    </modal-component>
    <modal-component
      :active="showModalColors"
      @cancel="showModalColors = false"
      @hdlOnClick="submitColorConfig"
      :textSave="`Guardar`"
      titleModal="Configuración de colores"
      optionalText="Define la paleta de colores que se aplicará en tu portal de onboarding."
      :disabled="false"
      :loader="loadingColors"
      :scrollBody="false"
      :isOverflow="true"
    >
      <div class="color-config-grid">
        <div class="color-config-item">
          <div>
            <span class="color-config-label">Color principal</span>
            <span class="color-config-desc">Identidad visual de la plataforma</span>
          </div>
          <color-picker v-model="colorValues.principal" />
        </div>
        <div class="color-config-item">
          <div>
            <span class="color-config-label">Color secundario</span>
            <span class="color-config-desc">Elementos de acento y destacados</span>
          </div>
          <color-picker v-model="colorValues.secondary" />
        </div>
        <div class="color-config-item">
          <div>
            <span class="color-config-label">Color header</span>
            <span class="color-config-desc">Barra de navegación superior</span>
          </div>
          <color-picker v-model="colorValues.header" />
        </div>
        <div class="color-config-item">
          <div>
            <span class="color-config-label">Color de botones principales</span>
            <span class="color-config-desc">Botones de acción primaria</span>
          </div>
          <color-picker v-model="colorValues.buttons" />
        </div>
        <div class="color-config-item">
          <div>
            <span class="color-config-label">Color de botones secundarios</span>
            <span class="color-config-desc">Botones de acción secundaria</span>
          </div>
          <color-picker v-model="colorValues.buttonsSecondary" />
        </div>
      </div>
    </modal-component>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import mixins from '../../mixins/formats'

export default {
  components: {
    TitleBar: () => import('@/components/ui/TitleBar.vue'),
    ModalComponent: () => import('@/components/ui/ModalComponent'),
    UploadInput: () => import('@/components/ui/UploadInput'),
    ColorPicker: () => import('@/components/ui/ColorPicker')
  },
  mixins: [mixins],
  data () {
    return {
      loading: false,
      assets: {
        logo: '',
        favicon: '',
        footer: '',
        colors: {
          principal: '',
          secondary: '',
          buttons: '',
          buttonsSecondary: '',
          header: ''
        }
      },
      showModalLogo: false,
      showModalFavi: false,
      showModalColors: false,
      logoFile: null,
      logoBase64: null,
      faviconFile: null,
      faviconBase64: null,
      loadingLogo: false,
      loadingFavicon: false,
      loadingColors: false,
      colorValues: {
        principal: '',
        secondary: '',
        buttons: '',
        buttonsSecondary: '',
        header: ''
      },
      websitesUrls: {},
      logoLink: ''
    }
  },
  methods: {
    ...mapActions(['getUIAssets', 'updateLogo', 'updateFavicon', 'updateColors', 'getWebsiteUrls']),
    handleLogoChange (file) {
      if (!file || !file.type.startsWith('image/')) {
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.logoBase64 = e.target.result
        this.logoFile = file
      }
      reader.readAsDataURL(file)
    },
    async submitLogoUpload () {
      this.loadingLogo = true
      try {
        if (!this.logoBase64) {
          return
        }
        const update = await this.updateLogo({ logoBase64: this.logoBase64, logoLink: this.logoLink })
        this.assets.logo = `${update.url}?t=${Date.now()}`
        await this.loadingVariables()
        this.showModalLogo = false
      } catch (error) {
      } finally {
        this.loadingLogo = false
      }
    },
    async submitColorConfig () {
      this.loadingColors = true
      try {
        if (!this.colorValues) {
          return
        }

        const cleanedColorValues = {
          principal: this.colorValues.principal || null,
          secondary: this.colorValues.secondary || null,
          buttons: this.colorValues.buttons || null,
          buttonsSecondary: this.colorValues.buttonsSecondary || null,
          header: this.colorValues.header || null
        }

        const colorsString = JSON.stringify(cleanedColorValues)
        const update = await this.updateColors({ colors: colorsString })

        if (update.success) {
          const response = await this.getUIAssets()
          if (response) {
            this.assets.logo = response.logo
            this.assets.favicon = response.favicon
            this.assets.footer = response.footer

            const parsedColors = JSON.parse(response.colors || '{}')
            this.assets.colors.principal = parsedColors.principal || null
            this.assets.colors.secondary = parsedColors.secondary || null
            this.assets.colors.buttons = parsedColors.buttons || null
            this.assets.colors.buttonsSecondary = parsedColors.buttonsSecondary || null
            this.assets.colors.header = parsedColors.header || null

            this.colorValues = {
              principal: parsedColors.principal || '',
              secondary: parsedColors.secondary || '',
              buttons: parsedColors.buttons || '',
              buttonsSecondary: parsedColors.buttonsSecondary || '',
              header: parsedColors.header || ''
            }
          }
        }
        this.showModalColors = false
      } catch (error) {
      } finally {
        this.loadingColors = false
      }
    },
    handleFaviconChange (file) {
      if (!file || !file.type.startsWith('image/')) {
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.faviconBase64 = e.target.result
        this.faviconFile = file
      }
      reader.readAsDataURL(file)
    },
    async submitFaviUpload () {
      this.loadingFavicon = true
      try {
        if (!this.faviconBase64) {
          return
        }
        const result = await this.updateFavicon({ faviconBase64: this.faviconBase64 })
        if (result) {
          this.assets.favicon = `${result.url}?t=${Date.now()}`
          this.showModalFavi = false
        }
      } catch (err) {
      } finally {
        this.loadingFavicon = false
      }
    },
    async loadingVariables () {
      try {
        const [response, websites] = await Promise.all([
          this.getUIAssets(),
          this.getWebsiteUrls()
        ])

        if (websites) {
          this.websitesUrls = websites
        }

        if (response) {
          this.assets.logo = response.logo
          this.assets.favicon = response.favicon
          this.assets.footer = response.footer
          this.assets.logoLink = response.logoLink

          const parsedColors = JSON.parse(response.colors || '{}')
          this.assets.colors.principal = parsedColors.principal || '-'
          this.assets.colors.secondary = parsedColors.secondary || '-'
          this.assets.colors.buttons = parsedColors.buttons || '-'
          this.assets.colors.buttonsSecondary = parsedColors.buttonsSecondary || '-'
          this.assets.colors.header = parsedColors.header || '-'
          this.colorValues = {
            principal: parsedColors.principal || '',
            secondary: parsedColors.secondary || '',
            buttons: parsedColors.buttons || '',
            buttonsSecondary: parsedColors.buttonsSecondary || '',
            header: parsedColors.header || ''
          }
        }
      } catch (error) {
      } finally {
        this.loadingLogo = false
      }
    }
  },
  computed: {
    ...mapState({
      systemConfiguration: state => state.configuration,
      profile: state => state.financialCompany.financialCompany
    })
  },
  watch: {
    showModalLogo (newVal) {
      if (!newVal) {
        this.logoBase64 = null
        this.logoFile = null
      }
    },
    showModalFavi (newVal) {
      if (!newVal) {
        this.faviconBase64 = null
        this.faviconFile = null
      }
    },
    showModalColors (newVal) {
      if (!newVal) {
        this.colorValues = {
          principal: this.assets.colors.principal || '',
          secondary: this.assets.colors.secondary || '',
          buttons: this.assets.colors.buttons || '',
          buttonsSecondary: this.assets.colors.buttonsSecondary || '',
          header: this.assets.colors.header || ''
        }
      }
    }
  },
  async created () {
    await this.loadingVariables()
  }
}
</script>

<style lang="scss" scoped>
// ── Page layout ──────────────────────────────────────────────

.wp-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

// ── Section ──────────────────────────────────────────────────
.wp-section {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  background: #ffffff;
  border: 1px solid #eef0f3;
  border-radius: 12px;
  padding: 1.75rem 2rem;
}
.wp-section__head {
  padding-right: 1rem;
  border-right: 1px solid #eef0f3;
}
.wp-section__title {
  font-size: 14px;
  font-weight: 600;
  color: #1a2233;
  margin: 0 0 6px;
}
.wp-section__desc {
  font-size: 12px;
  color: #9aa5b4;
  line-height: 1.5;
  margin: 0;
}

.wp-section__content {
  display: flex;
  flex-direction: column;
}

// ── Divider ──────────────────────────────────────────────────
.wp-divider {
  height: 1px;
  background: #eef0f3;
  margin: 4px 0;
}

// ── Edit button ──────────────────────────────────────────────
.wp-btn-edit {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #4c70ff;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: color 0.15s;

  &:hover {
    color: darken(#4c70ff, 10%);
    text-decoration: underline;
  }
}

// ── Asset block ──────────────────────────────────────────────
.wp-asset {
  padding: 0.5rem 0;
}
.wp-asset__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.wp-asset__label {
  font-size: 12px;
  font-weight: 500;
  color: #9aa5b4;
}
.wp-asset__preview {
  display: flex;
  align-items: center;
  min-height: 48px;
}
.wp-asset__empty {
  font-size: 13px;
  color: #c0c8d4;
  font-style: italic;
}
.wp-asset__link {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: #9aa5b4;

  i {
    font-size: 10px;
  }
  a {
    color: #4c70ff;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
}
.wp-logo {
  max-height: 60px;
  max-width: 180px;
  object-fit: contain;
}
.wp-favicon {
  height: 28px;
  width: 28px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #eef0f3;
}

// ── Colors ───────────────────────────────────────────────────
.wp-colors-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.wp-color-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0;
  min-height: 44px;

  &__label {
    font-size: 12px;
    font-weight: 500;
    color: #9aa5b4;
  }
  &__value {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__hex {
    font-size: 13px;
    font-weight: 500;
    color: #2d3748;
    font-family: monospace;
  }
  &__empty {
    font-size: 13px;
    color: #c0c8d4;
  }
}

.wp-swatch {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

// ── URLs ─────────────────────────────────────────────────────
.wp-url-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0;
  min-height: 44px;

  &__label {
    font-size: 12px;
    font-weight: 500;
    color: #9aa5b4;
    min-width: 160px;
  }
  &__link {
    font-size: 13px;
    color: #4c70ff;
    text-decoration: none;
    word-break: break-all;
    text-align: right;

    &:hover { text-decoration: underline; }
  }
  &__empty {
    font-size: 13px;
    color: #c0c8d4;
  }
}

// ── Modal internals (pay-content animation) ──────────────────
.pay-content {
  animation: 0.5s pay_content_effet;
  @keyframes pay_content_effet {
    0% { opacity: 0; }
    80% { opacity: 1; }
  }
}

// ── Color config modal ───────────────────────────────────────
.color-config-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.color-config-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fc;
  border-radius: 10px;
  border: 1px solid #eef0f3;
}

.color-config-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1a2233;
}

.color-config-desc {
  display: block;
  font-size: 11px;
  color: #9aa5b4;
  margin-top: 2px;
}

.description {
  border-bottom: 1px solid #e3e3e3;
  font-size: 13px;
  padding-bottom: 0.2rem;
  margin-bottom: 0.8rem;
}

.logo-link-section {
  margin-top: 1.5rem;
}

.modal-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #edf1ff;
  border: 1px solid #c5d0ff;
  border-radius: 8px;
  padding: 10px 14px;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 500;
  color: #283056;
  line-height: 1.5;

  &__icon {
    color: #4c70ff;
    font-size: 13px;
    margin-top: 1px;
    flex-shrink: 0;
  }
}
</style>
